import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { AddressType } from '@sg/garnish';
import { theme, VStack } from '@sg/garnish';
import type { CourierDetails } from '@sg/graphql-schema';
import type { Ledger } from '@sg/graphql-schema';
import {
  type DeliveryPreferenceType,
  FlattenedOrderStatuses,
} from '@sg/graphql-schema';

import { type PartialLineItem } from '../../../../graphql';
import {
  useHasEarnedLoyaltyPoints,
  type useOrderStatusRequest,
} from '../../hooks';
import { OrderStatusAddress } from '../OrderStatusAddress';
import { OrderStatusCourierInfo } from '../OrderStatusCourierInfo';
import { OrderStatusLedger } from '../OrderStatusLedger';
import { OrderStatusLoyaltyPointsBanner } from '../OrderStatusLoyaltyPointsBanner';
import { OrderStatusPayments } from '../OrderStatusPayments';

export const OrderStatusDetails = (props: OrderStatusDetailsProps) => {
  const {
    orderId,
    orderType,
    orderStatus,
    isOrderCancelled,
    courierName,
    courierPhone,
    payments,
  } = props;

  const hasEarnedLoyaltyPoints = useHasEarnedLoyaltyPoints(
    orderId,
    isOrderCancelled,
  );

  const shouldShowCourierInfo =
    orderType === 'delivery' &&
    orderStatus === FlattenedOrderStatuses.Delivering &&
    courierName &&
    courierPhone;

  const shouldShowPayments = payments.length > 0;

  return (
    <VStack gap={0} divider={<Divider />}>
      {hasEarnedLoyaltyPoints ? <OrderStatusLoyaltyPointsBanner /> : null}

      {shouldShowCourierInfo ? <OrderStatusCourierInfo {...props} /> : null}

      <OrderStatusAddress {...props} />

      <OrderStatusLedger {...props} />

      {shouldShowPayments ? (
        <OrderStatusPayments payments={props.payments} />
      ) : null}
    </VStack>
  );
};

const Divider = () => {
  return <View style={styles.divider} />;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  divider: {
    height: 8,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    marginHorizontal: -theme.spacing['10'],
    marginVertical: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusDetailsProps = Readonly<{
  orderId: string;
  orderType: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  payments: ReturnType<typeof useOrderStatusRequest>['payments'];
  isOrderCancelled: boolean;
  deliveryAddressName?: string;
  deliveryAddressStreet?: string;
  deliveryAddressSecondaryStreet?: string;
  deliveryAddressCity?: string;
  deliveryAddressState?: string;
  deliveryAddressZipCode?: string;
  deliveryAddressNotes?: string;
  deliveryPreference?: DeliveryPreferenceType;
  locationName?: string;
  locationAddress?: string;
  locationCity?: string;
  locationState?: string;
  locationZipCode?: string;
  lineItems: readonly PartialLineItem[];
  dropoffLocation?: string;
  ledger: Partial<Ledger>;
  courierName?: CourierDetails['name'];
  courierPhone?: CourierDetails['phone'];
}>;
